import { anchorForIndex, cardIdsFor, moveCard } from '@blueprint/domain'
import { useEffect, useState } from 'react'
import { Dialog } from '../../components/Dialog.js'
import { Button, Field, Select } from '../../components/controls.js'
import { useBoardStore } from '../../state/board-store.js'
import { useUiStore } from '../../state/ui-store.js'

type Placement = 'top' | 'bottom'

/** Keyboard- and screen-reader-friendly alternative to dragging a card. */
export function MoveCardDialog() {
  const cardId = useUiStore((state) => state.moveCardId)
  const closeMove = useUiStore((state) => state.closeMove)
  const entities = useBoardStore((state) => state.entities)
  const card = cardId ? entities?.cards[cardId] : undefined

  const [rowId, setRowId] = useState('')
  const [columnId, setColumnId] = useState('')
  const [placement, setPlacement] = useState<Placement>('bottom')

  useEffect(() => {
    if (!card) return
    setRowId(card.rowId)
    setColumnId(card.columnId)
    setPlacement('bottom')
  }, [card?.id])

  if (!cardId || !card || !entities) return null

  const submit = () => {
    useBoardStore.getState().run((state) => {
      const destination = cardIdsFor(state, rowId, columnId).filter((id) => id !== cardId)
      const index = placement === 'top' ? 0 : destination.length
      const beforeId = anchorForIndex(state, rowId, columnId, index, cardId)
      return moveCard(state, cardId, { rowId, columnId, beforeId })
    })
    closeMove()
  }

  return (
    <Dialog
      open
      title={`Move "${card.title}"`}
      onClose={closeMove}
      onSubmit={submit}
      footer={
        <>
          <Button onClick={closeMove}>Cancel</Button>
          <Button variant="primary" type="submit">
            Move card
          </Button>
        </>
      }
    >
      <Field label="Row">
        {(id) => (
          <Select id={id} data-autofocus value={rowId} onChange={(event) => setRowId(event.target.value)}>
            {entities.swimlaneIds.map((swimlaneId) => (
              <optgroup key={swimlaneId} label={entities.swimlanes[swimlaneId]?.title ?? 'Swimlane'}>
                {(entities.rowIdsBySwimlane[swimlaneId] ?? []).map((id) => (
                  <option key={id} value={id}>
                    {entities.rows[id]?.title ?? 'Row'}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
        )}
      </Field>

      <div style={{ height: 12 }} />

      <Field label="Column">
        {(id) => (
          <Select id={id} value={columnId} onChange={(event) => setColumnId(event.target.value)}>
            {entities.columnIds.map((id) => (
              <option key={id} value={id}>
                {entities.columns[id]?.title ?? 'Column'}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <div style={{ height: 12 }} />

      <Field label="Position in cell">
        {(id) => (
          <Select id={id} value={placement} onChange={(event) => setPlacement(event.target.value as Placement)}>
            <option value="top">Top</option>
            <option value="bottom">Bottom</option>
          </Select>
        )}
      </Field>
    </Dialog>
  )
}
